import * as React from "react";

import Box from "@mui/material/Box";

interface ArticleBodyProps {
    /**
     * Article HTML. Sanitised server-side at import and on every admin write
     * against a tag/attribute allowlist, which is why it can be injected here.
     * Never pass unsanitised HTML to this component.
     */
    html: string;
}

/**
 * Renders a help article body.
 *
 * Intercom's own stylesheet isn't present, so the article arrives as bare
 * semantic HTML and needs its own typography, spacing, and media rules to be
 * readable. Shared with the admin preview so reviewers see exactly what a user
 * will see before publishing.
 */
export default function ArticleBody({
    html,
}: ArticleBodyProps): React.JSX.Element {
    return (
        <Box
            sx={{
                fontSize: 16,
                lineHeight: 1.6,
                wordBreak: "break-word",
                "& h1, & h2, & h3, & h4": {
                    lineHeight: 1.3,
                    mt: 3,
                    mb: 1,
                    "&:first-of-type": { mt: 0 },
                },
                "& h1": { fontSize: 24 },
                "& h2": { fontSize: 20 },
                "& h3": { fontSize: 17 },
                "& p": { my: 1.5 },
                "& ul, & ol": { pl: 3, my: 1.5 },
                "& li": { my: 0.5 },
                "& a": { color: "info.main" },
                "& strong, & b": { fontWeight: 600 },
                "& blockquote": {
                    borderLeft: "3px solid",
                    borderColor: "divider",
                    pl: 2,
                    ml: 0,
                    my: 2,
                    fontStyle: "italic",
                },
                "& code": {
                    fontFamily: "monospace",
                    fontSize: "0.9em",
                    bgcolor: "action.hover",
                    px: 0.5,
                    borderRadius: 0.5,
                },
                "& pre": {
                    bgcolor: "action.hover",
                    p: 1.5,
                    borderRadius: 1,
                    overflowX: "auto",
                },
                // Images come from the Intercom CDN at their authored size;
                // constrain them so a wide screenshot can't force the article
                // column to scroll sideways on a phone.
                "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                    my: 2,
                    borderRadius: 1,
                },
                "& figure": { m: 0, my: 2 },
                "& figcaption": {
                    fontSize: 13,
                    color: "text.secondary",
                    mt: 0.5,
                },
                "& iframe": {
                    maxWidth: "100%",
                    border: 0,
                    aspectRatio: "16 / 9",
                    width: "100%",
                    my: 2,
                },
                // Tables scroll within their own container rather than
                // widening the page.
                "& table": {
                    display: "block",
                    overflowX: "auto",
                    borderCollapse: "collapse",
                    my: 2,
                    maxWidth: "100%",
                },
                "& th, & td": {
                    border: "1px solid",
                    borderColor: "divider",
                    px: 1,
                    py: 0.75,
                    textAlign: "left",
                },
                "& hr": {
                    border: 0,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    my: 3,
                },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}
